import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { COLOR_THEMES, DEFAULT_THEME_ID } from '../config/themes';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const applyThemeVariables = (themeId) => {
  const theme = COLOR_THEMES[themeId] || COLOR_THEMES[DEFAULT_THEME_ID];
  const root = document.documentElement;
  root.setAttribute('data-theme', themeId);

  Object.entries(theme.vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('adminDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const [colorTheme, setColorThemeState] = useState(() => {
    const saved = localStorage.getItem('adminColorTheme');
    return saved && COLOR_THEMES[saved] ? saved : DEFAULT_THEME_ID;
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('adminSidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('adminDarkMode', JSON.stringify(isDarkMode));
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  useEffect(() => {
    localStorage.setItem('adminColorTheme', colorTheme);
    applyThemeVariables(colorTheme);
  }, [colorTheme]);

  useEffect(() => {
    localStorage.setItem('adminSidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => !prev);
  }, []);

  const setColorTheme = useCallback((themeId) => {
    if (COLOR_THEMES[themeId]) {
      setColorThemeState(themeId);
    }
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const currentTheme = COLOR_THEMES[colorTheme] || COLOR_THEMES[DEFAULT_THEME_ID];
  const availableThemes = Object.values(COLOR_THEMES);

  const value = {
    isDarkMode,
    toggleDarkMode,
    colorTheme,
    setColorTheme,
    currentTheme,
    availableThemes,
    sidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebar,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
